/**
 * QaManagerOverview
 * =================
 *
 * The QA Manager's home page (2026-07-15).
 *
 * Two tabs:
 *   1. Team — per-evaluator totals (assigned / evaluated / coached)
 *      with click-through to filter the evaluations tab.
 *   2. Evaluations — filterable table of every QA evaluation in the
 *      manager's tenant, with inline actions:
 *        * View / Edit  → opens PlaybackPopout with eval panel
 *        * Reassign     → modal to pick new evaluator + reason
 *        * Coached      → toggles coached_at + prompts for notes
 *        * Delete       → hard delete (frees recording)
 *
 * Tenancy is enforced server-side; this page simply consumes /qa/evaluations.
 *
 * Route: `/qa-manager` (gated by RoleGuard allow=['admin','qa_manager']).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaApi } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';

const STATUSES = ['', 'draft', 'submitted', 'acknowledged', 'disputed'];

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function QaManagerOverview() {
  const [tab, setTab] = useState('team');
  const [filterStatus,      setFilterStatus]      = useState('');
  const [filterEvaluator,   setFilterEvaluator]   = useState('');
  const [filterAgent,       setFilterAgent]       = useState('');
  const [filterCoached,     setFilterCoached]     = useState('');   // '', 'yes', 'no'
  const [reassignFor, setReassignFor] = useState(null);   // eval row
  const [coachFor,    setCoachFor]    = useState(null);
  // 2026-07-15 pm: Unassigned tab (recordings without a QA evaluation).
  const [unassignedDays,   setUnassignedDays]   = useState(30);
  const [assignFor,        setAssignFor]        = useState(null); // row from unassigned list

  const evalsQ = useQuery({
    queryKey: ['qa','evaluations', filterStatus, filterEvaluator, filterAgent, filterCoached],
    queryFn: () => qaApi.listEvaluations({
      status: filterStatus || undefined,
      evaluator_id: filterEvaluator || undefined,
      agent_user_id: filterAgent || undefined,
      coached: filterCoached === 'yes' ? true : filterCoached === 'no' ? false : undefined,
      limit: 200,
    }),
    staleTime: 15_000,
  });
  const evals = evalsQ.data || [];

  // Team totals derived from the same list (client-side aggregate keeps
  // the initial deploy simple; if it doesn't scale we'll add a backend
  // aggregate endpoint later).
  const team = useMemo(() => {
    const by = new Map();
    for (const e of evals) {
      const key = e.evaluator_id || '_unassigned';
      if (!by.has(key)) by.set(key, {
        evaluator_id:   e.evaluator_id,
        evaluator_name: e.evaluator_name || '—',
        assigned: 0, evaluated: 0, coached: 0,
      });
      const row = by.get(key);
      if (e.status === 'draft')                                       row.assigned  += 1;
      else if (['submitted','acknowledged','disputed'].includes(e.status)) row.evaluated += 1;
      if (e.coached_at) row.coached += 1;
    }
    return Array.from(by.values()).sort((a, b) => (a.evaluator_name || '').localeCompare(b.evaluator_name || ''));
  }, [evals]);

  const openPlayback = (recordingId) => {
    const url = `${window.location.origin}/playback/${recordingId}?evaluate=1`;
    window.open(url, `cx-playback-${recordingId}`, 'width=1400,height=900');
  };

  const del = async (evalRow) => {
    if (!confirm(`Delete evaluation by ${evalRow.evaluator_name || 'unknown'}? Frees the recording for a fresh evaluation. Cannot be undone.`)) return;
    try {
      await qaApi.deleteEvaluation(evalRow.id);
      evalsQ.refetch();
    } catch (err) {
      alert(`Delete failed: ${err?.detail || err?.message || err}`);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">QA Manager — Overview</h1>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 rounded-thin text-sm ${tab === 'team' ? 'bg-continuum-600 text-white' : 'border'}`}
            onClick={() => setTab('team')}
          >👥 Team</button>
          <button
            className={`px-3 py-1 rounded-thin text-sm ${tab === 'unassigned' ? 'bg-continuum-600 text-white' : 'border'}`}
            onClick={() => setTab('unassigned')}
          >📝 Unassigned</button>
          <button
            className={`px-3 py-1 rounded-thin text-sm ${tab === 'evals' ? 'bg-continuum-600 text-white' : 'border'}`}
            onClick={() => setTab('evals')}
          >✅ Evaluations</button>
        </div>
      </div>

      {tab === 'team' && (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Evaluator</th>
                <th className="px-3 py-2 text-right">Assigned (draft)</th>
                <th className="px-3 py-2 text-right">Evaluated</th>
                <th className="px-3 py-2 text-right">Coached</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {team.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-surface-muted">No evaluations in your tenant yet.</td></tr>
              )}
              {team.map((row) => (
                <tr key={row.evaluator_id || '_unassigned'} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2">{row.evaluator_name}</td>
                  <td className="px-3 py-2 text-right">{row.assigned}</td>
                  <td className="px-3 py-2 text-right">{row.evaluated}</td>
                  <td className="px-3 py-2 text-right">{row.coached}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="text-continuum-600 dark:text-continuum-200 text-sm"
                      onClick={() => { setTab('evals'); setFilterEvaluator(row.evaluator_id || ''); }}
                    >View evaluations →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'evals' && (
        <>
          <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-1 rounded-thin border">
              <option value="">All statuses</option>
              {STATUSES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterCoached} onChange={(e) => setFilterCoached(e.target.value)}
              className="px-2 py-1 rounded-thin border">
              <option value="">Coached: any</option>
              <option value="yes">Coached: yes</option>
              <option value="no">Coached: no</option>
            </select>
            <input type="text" value={filterEvaluator} onChange={(e) => setFilterEvaluator(e.target.value)}
              placeholder="Filter by evaluator id"
              className="px-2 py-1 rounded-thin border w-64" />
            <button
              className="px-2 py-1 rounded-thin border"
              onClick={() => { setFilterStatus(''); setFilterEvaluator(''); setFilterAgent(''); setFilterCoached(''); }}
            >Clear</button>
            <button
              className="ml-auto px-2 py-1 rounded-thin border"
              onClick={() => evalsQ.refetch()}
            >{evalsQ.isFetching ? 'Refreshing…' : '↻ Refresh'}</button>
          </div>

          <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
                <tr>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Evaluator</th>
                  <th className="px-3 py-2 text-left">Form</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Score</th>
                  <th className="px-3 py-2 text-left">Coached</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {evals.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">No evaluations match these filters.</td></tr>
                )}
                {evals.map((ev) => (
                  <tr key={ev.id} className="border-t border-surface-border dark:border-surface-dark-border">
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                    <td className="px-3 py-2 text-xs">{ev.evaluator_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">{ev.form_name}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle">{ev.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-right">
                      {ev.overall_score !== null && ev.overall_score !== undefined
                        ? Number(ev.overall_score).toFixed(1)
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {ev.coached_at ? (
                        <span className="text-blue-600 dark:text-blue-300" title={`by ${ev.coached_by_name || 'unknown'}`}>
                          🎓 Yes
                        </span>
                      ) : (
                        <span className="text-surface-muted">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right space-x-2 whitespace-nowrap">
                      <button
                        className="text-continuum-600 dark:text-continuum-200"
                        onClick={() => openPlayback(ev.recording_id)}
                      >View/Edit</button>
                      <button
                        className="text-blue-600 dark:text-blue-300"
                        onClick={() => setCoachFor(ev)}
                        disabled={ev.status === 'draft'}
                        title={ev.status === 'draft' ? 'Cannot coach a draft' : 'Toggle coached state'}
                      >{ev.coached_at ? 'Uncoach' : 'Mark coached'}</button>
                      <button
                        className="text-amber-600 dark:text-amber-300"
                        onClick={() => setReassignFor(ev)}
                      >Reassign</button>
                      <button
                        className="text-rose-600 dark:text-rose-400"
                        onClick={() => del(ev)}
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'unassigned' && (
        <UnassignedTab
          days={unassignedDays}
          onDays={setUnassignedDays}
          onAssign={setAssignFor}
        />
      )}

      {assignFor && (
        <QuickAssignModal
          recording={assignFor}
          onClose={() => setAssignFor(null)}
          onDone={() => { setAssignFor(null); evalsQ.refetch(); }}
        />
      )}

      {reassignFor && (
        <ReassignModal
          evalRow={reassignFor}
          onClose={() => setReassignFor(null)}
          onDone={() => { setReassignFor(null); evalsQ.refetch(); }}
        />
      )}

      {coachFor && (
        <CoachedModal
          evalRow={coachFor}
          onClose={() => setCoachFor(null)}
          onDone={() => { setCoachFor(null); evalsQ.refetch(); }}
        />
      )}
    </div>
  );
}


function ReassignModal({ evalRow, onClose, onDone }) {
  const [newEvaluatorId, setNewEvaluatorId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // 2026-07-15 follow-up: fetch tenant-scoped list of eligible evaluators
  // so the QA Manager picks from a dropdown instead of pasting a UUID.
  const evaluatorsQ = useQuery({
    queryKey: ['qa', 'eligible-evaluators'],
    queryFn:  () => qaApi.eligibleEvaluators(),
    staleTime: 60_000,
  });
  const evaluators = evaluatorsQ.data || [];

  const submit = async () => {
    if (!newEvaluatorId) { setErr('Pick a new evaluator.'); return; }
    if (newEvaluatorId === evalRow.evaluator_id) {
      setErr('That is the current evaluator.'); return;
    }
    setBusy(true); setErr(null);
    try {
      await qaApi.reassign(evalRow.id, { new_evaluator_id: newEvaluatorId, reason });
      onDone();
    } catch (e) {
      setErr(e?.detail || e?.message || 'Reassign failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold mb-2">Reassign evaluation</h3>
      <p className="text-sm mb-3 text-surface-muted">
        Currently assigned to <strong>{evalRow.evaluator_name || '—'}</strong>.<br/>
        Reassigning will wipe any existing answers and hand the evaluation
        to the new user as a fresh draft.
      </p>
      <label className="block text-xs mb-1">New evaluator</label>
      {evaluatorsQ.isLoading ? (
        <div className="text-xs text-surface-muted mb-3">Loading users…</div>
      ) : evaluatorsQ.isError ? (
        <div className="text-xs text-rose-600 mb-3">
          Could not load users — {String(evaluatorsQ.error?.detail || evaluatorsQ.error?.message || evaluatorsQ.error)}
        </div>
      ) : (
        <select
          value={newEvaluatorId}
          onChange={(e) => setNewEvaluatorId(e.target.value)}
          className="w-full px-2 py-1 rounded-thin border mb-3 text-sm bg-surface-base dark:bg-surface-dark-base"
        >
          <option value="">— pick a user —</option>
          {evaluators.map((u) => (
            <option key={u.id} value={u.id} disabled={u.id === evalRow.evaluator_id}>
              {(u.name || u.email || u.id.slice(0, 8))}
              {u.extension ? ` (ext ${u.extension})` : ''}
              {' · '}{u.role}
              {u.id === evalRow.evaluator_id ? ' — current' : ''}
            </option>
          ))}
        </select>
      )}
      <label className="block text-xs mb-1">Reason (optional)</label>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full px-2 py-1 rounded-thin border mb-3"
        placeholder="Why is this being reassigned?"
      />
      {err && <div className="text-xs text-rose-600 mb-2">{err}</div>}
      <div className="flex justify-end gap-2">
        <button className="px-3 py-1 rounded-thin border" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="px-3 py-1 rounded-thin bg-amber-600 text-white" onClick={submit} disabled={busy || !newEvaluatorId}>
          {busy ? 'Reassigning…' : 'Reassign'}
        </button>
      </div>
    </Overlay>
  );
}


function CoachedModal({ evalRow, onClose, onDone }) {
  const currentlyCoached = !!evalRow.coached_at;
  const [notes, setNotes] = useState(evalRow.coached_notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await qaApi.coached(evalRow.id, {
        coached: !currentlyCoached,
        coached_notes: !currentlyCoached ? notes : null,
      });
      onDone();
    } catch (e) {
      setErr(e?.detail || e?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold mb-2">
        {currentlyCoached ? 'Clear coached state' : 'Mark evaluation as coached'}
      </h3>
      <p className="text-sm mb-3 text-surface-muted">
        Evaluation by <strong>{evalRow.evaluator_name || '—'}</strong>, form <strong>{evalRow.form_name}</strong>.
      </p>
      {!currentlyCoached && (
        <>
          <label className="block text-xs mb-1">Coaching notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-2 py-1 rounded-thin border mb-3 h-24"
            placeholder="Session outcomes, follow-ups, links..."
          />
        </>
      )}
      {err && <div className="text-xs text-rose-600 mb-2">{err}</div>}
      <div className="flex justify-end gap-2">
        <button className="px-3 py-1 rounded-thin border" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="px-3 py-1 rounded-thin bg-blue-600 text-white" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : (currentlyCoached ? 'Clear coached' : 'Mark coached')}
        </button>
      </div>
    </Overlay>
  );
}


function Overlay({ children, onClose }) {
  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 9998,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--cx-panel, #fff)', color: 'var(--cx-text, #0f172a)',
                    padding: 20, borderRadius: 8, maxWidth: 480, width: '100%',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.35)' }}>
        {children}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// UnassignedTab (2026-07-15 pm) + QuickAssignModal
// ---------------------------------------------------------------------
// Lists recordings without a submitted evaluation. QA Manager can click
// Assign to send one to an evaluator (creates a draft evaluation).

function UnassignedTab({ days, onDays, onAssign }) {
  const q = useQuery({
    queryKey: ['qa', 'unassigned', days],
    queryFn:  () => qaApi.unassignedRecordings({ days, limit: 200, include_drafts: true }),
    staleTime: 15_000,
  });
  const rows = q.data || [];
  return (
    <>
      <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
        <label>Window:</label>
        <select value={days} onChange={(e) => onDays(Number(e.target.value))}
          className="px-2 py-1 rounded-thin border">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <div className="ml-auto text-surface-muted">{rows.length} recording{rows.length === 1 ? '' : 's'}</div>
        <button onClick={() => q.refetch()}
          className="px-2 py-1 rounded-thin border">{q.isFetching ? 'Refreshing…' : '↻ Refresh'}</button>
      </div>

      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left">Started</th>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-left">Queue</th>
              <th className="px-3 py-2 text-left">Caller</th>
              <th className="px-3 py-2 text-right">Duration</th>
              <th className="px-3 py-2 text-left">Draft?</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">Loading…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">
                No recordings need assignment in the last {days} day{days === 1 ? '' : 's'}.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.recording_id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(r.started_at)}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{r.agent_name || '—'}</div>
                  <div className="text-surface-muted">{r.agent_extension || ''}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.queue_name ? `${r.queue_id} · ${r.queue_name}` : (r.queue_id || '—')}</td>
                <td className="px-3 py-2 text-xs">{r.caller_id || '—'}</td>
                <td className="px-3 py-2 text-xs text-right">{Math.round(r.duration_sec || 0)}s</td>
                <td className="px-3 py-2 text-xs">
                  {r.has_draft ? (
                    <span className="text-amber-600 dark:text-amber-300" title={`Draft with ${r.draft_evaluator_name || 'unknown'}`}>
                      📝 with {r.draft_evaluator_name || 'evaluator'}
                    </span>
                  ) : (
                    <span className="text-surface-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-right space-x-2 whitespace-nowrap">
                  <button
                    className="text-continuum-600 dark:text-continuum-200"
                    onClick={() => {
                      const url = `${window.location.origin}/playback/${r.recording_id}`;
                      window.open(url, `cx-playback-${r.recording_id}`, 'width=1400,height=900');
                    }}
                  >👁 View</button>
                  <button
                    className="text-emerald-600 dark:text-emerald-400"
                    onClick={() => onAssign(r)}
                  >{r.has_draft ? 'Reassign' : '🎯 Assign'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


function QuickAssignModal({ recording, onClose, onDone }) {
  const [evaluatorId, setEvaluatorId] = useState('');
  const [formId, setFormId]           = useState('');
  const [busy, setBusy]               = useState(false);
  const [err, setErr]                 = useState(null);

  const evaluatorsQ = useQuery({
    queryKey: ['qa','eligible-evaluators'],
    queryFn:  () => qaApi.eligibleEvaluators(),
    staleTime: 60_000,
  });
  const formsQ = useQuery({
    queryKey: ['qa','forms','active'],
    queryFn:  () => qaApi.listForms(true),
    staleTime: 60_000,
  });
  const evaluators = evaluatorsQ.data || [];
  const forms = formsQ.data || [];

  // Auto-select the only form if there's just one.
  React.useEffect(() => {
    if (!formId && forms.length === 1) setFormId(forms[0].id);
  }, [forms, formId]);

  const submit = async () => {
    if (!evaluatorId) { setErr('Pick an evaluator.'); return; }
    if (!formId)      { setErr('Pick a form.'); return; }
    setBusy(true); setErr(null);
    try {
      await qaApi.quickAssign({
        recording_id: recording.recording_id,
        form_id:      formId,
        evaluator_id: evaluatorId,
      });
      onDone();
    } catch (e) {
      setErr(e?.detail || e?.message || 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold mb-2">
        {recording.has_draft ? 'Reassign call' : 'Assign call for evaluation'}
      </h3>
      <p className="text-sm mb-3 text-surface-muted">
        <strong>{recording.agent_name || recording.agent_extension || '—'}</strong>
        {' · '}{Math.round(recording.duration_sec || 0)}s
        {' · '}{recording.caller_id || '—'}
        {recording.queue_id ? ` · queue ${recording.queue_id}` : ''}
        <br/>
        Started {fmtDate(recording.started_at)}
      </p>

      <label className="block text-xs mb-1">Evaluator</label>
      {evaluatorsQ.isLoading ? (
        <div className="text-xs text-surface-muted mb-3">Loading users…</div>
      ) : (
        <select
          value={evaluatorId}
          onChange={(e) => setEvaluatorId(e.target.value)}
          className="w-full px-2 py-1 rounded-thin border mb-3 text-sm bg-surface-base dark:bg-surface-dark-base"
        >
          <option value="">— pick an evaluator —</option>
          {evaluators.map((u) => (
            <option key={u.id} value={u.id}>
              {(u.name || u.email || u.id.slice(0, 8))}
              {u.extension ? ` (ext ${u.extension})` : ''}
              {' · '}{u.role}
            </option>
          ))}
        </select>
      )}

      <label className="block text-xs mb-1">Form</label>
      {formsQ.isLoading ? (
        <div className="text-xs text-surface-muted mb-3">Loading forms…</div>
      ) : forms.length === 0 ? (
        <div className="text-xs text-rose-600 mb-3">No active QA forms. Create one in Admin → QA Forms first.</div>
      ) : (
        <select
          value={formId}
          onChange={(e) => setFormId(e.target.value)}
          className="w-full px-2 py-1 rounded-thin border mb-3 text-sm bg-surface-base dark:bg-surface-dark-base"
        >
          <option value="">— pick a form —</option>
          {forms.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      )}

      {err && <div className="text-xs text-rose-600 mb-2">{err}</div>}
      <div className="flex justify-end gap-2">
        <button className="px-3 py-1 rounded-thin border" onClick={onClose} disabled={busy}>Cancel</button>
        <button
          className="px-3 py-1 rounded-thin bg-emerald-600 text-white"
          onClick={submit}
          disabled={busy || !evaluatorId || !formId}
        >
          {busy ? 'Assigning…' : (recording.has_draft ? 'Reassign' : 'Assign')}
        </button>
      </div>
    </Overlay>
  );
}
